import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import WaitingIcon from '../../_icons/WaitingIcon';

const GolfSpotUserInfo = ({ spotData, isGuestMode, onWaitingListClick }) => {
  const { t } = useTranslation();
  const getStatusLabel = spotStatus => {
    switch (spotStatus) {
      case 'IN_USE':
        return t('In use');
      case 'AVAILABLE':
        return t('Available for reservation');
      case 'UNAVAILABLE':
        return t('Unavailable for reservation');
      default:
        return t('Available for reservation');
    }
  };

  const getStatusColor = spotStatus => {
    switch (spotStatus) {
      case 'IN_USE':
        return '#0F7F85';
      case 'AVAILABLE':
        return '#0F7F85';
      case 'UNAVAILABLE':
        return '#A6A6A6';
      default:
        return '#0F7F85';
    }
  };

  // ISO 8601 형식의 시간을 시분 형식으로 변환
  const formatTimeFromISO = isoString => {
    if (!isoString) return '';
    try {
      const date = new Date(isoString);
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    } catch (error) {
      // 이미 시분 형식이거나 파싱 실패 시 그대로 반환
      return isoString;
    }
  };

  // 종료시간까지 남은 시간(분) 계산
  const calculateRemainingMinutesFromEndTime = endTimeISO => {
    if (!endTimeISO) return null;
    try {
      const endDate = new Date(endTimeISO);
      const now = new Date();
      const diffMs = endDate - now;
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      return diffMinutes > 0 ? diffMinutes : 0;
    } catch (error) {
      return null;
    }
  };

  const spotNumber = spotData?.spotNumber || spotData?.id?.replace('S', '') || '6';
  const spotStatus = spotData?.spotStatus || 'AVAILABLE';
  const startTime = formatTimeFromISO(spotData?.reservation?.startTime) || spotData?.startTime || '';
  const endTime = formatTimeFromISO(spotData?.reservation?.endTime) || spotData?.endTime || '';
  const remainingMinutes =
    calculateRemainingMinutesFromEndTime(spotData?.reservation?.endTime) || spotData?.remainingMinutes || null;
  const additionalMembers = spotData?.additionalMembers || 3;
  // 스팟 이동 기능: reservation이 있고 (RUNNING 또는 RESERVED) 상태이면 예약 정보 표시
  const hasReservation =
    spotData?.reservation && (spotData.reservation.status === 'RUNNING' || spotData.reservation.status === 'RESERVED');
  const watingUi = spotData?.waitings > 0;
  return (
    <Box
      sx={{
        width: '440px',
        height: '257px',
        border: '1px solid #D9D9D9',
        borderRadius: '8px',
        p: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
      }}>
      {/* 스팟 번호 & 상태 */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <Box
            sx={{
              width: '52px',
              height: '52px',
              border: '2px solid #737373',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <Typography sx={{ fontSize: '28px', fontWeight: '600', color: '#737373' }}>{spotNumber}</Typography>
          </Box>
          {(hasReservation || isGuestMode) && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <Typography sx={{ fontSize: '24px', fontWeight: '500', color: '#000' }}>
                  {spotData?.reservation?.memberName || '게스트'}
                  {/* {isGuestMode ? '게스트' : spotData?.reservation?.memberName || '게스트'} */}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#A6A6A6' }}>
                {spotData?.reservation?.memberPhoneNumber}
              </Typography>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            border: `1px solid ${getStatusColor(spotStatus)}`,
            borderRadius: '999px',
            px: '12px',
            py: '4px',
          }}>
          <Typography sx={{ fontSize: '16px', fontWeight: '500', color: getStatusColor(spotStatus) }}>
            {getStatusLabel(spotStatus)}
          </Typography>
        </Box>
      </Box>

      {/* 이용 시간 */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {hasReservation ? (
          <>
            <Typography sx={{ fontSize: '24px', fontWeight: '500', color: '#000' }}>
              {startTime} ~ {endTime}
            </Typography>
            {remainingMinutes !== null && remainingMinutes > 0 && (
              <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <Typography sx={{ fontSize: '24px', fontWeight: '500', color: '#0F7F85' }}>
                  {remainingMinutes}분
                </Typography>
                <Typography sx={{ fontSize: '16px', fontWeight: '500', color: '#000' }}>남음</Typography>
              </Box>
            )}
          </>
        ) : (
          <Typography sx={{ fontSize: '24px', fontWeight: '500', color: '#A6A6A6' }}>
            {t('No member is currently using')}
          </Typography>
        )}
      </Box>

      {/* 대기자 현황 */}
      {watingUi && (
        <Button
          fullWidth
          onClick={() => onWaitingListClick()}
          sx={{
            height: '52px',
            border: '1px solid #D9D9D9',
            borderRadius: '8px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: '16px',
            textTransform: 'none',
            '&:hover': { backgroundColor: '#F2F2F2' },
          }}>
          <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <WaitingIcon width={32} height={24} />
            <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              <Typography sx={{ fontSize: '20px', fontWeight: '500', color: '#000' }}>{t('Waiting')}</Typography>
              <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <Typography sx={{ fontSize: '20px', fontWeight: '500', color: '#0F7F85' }}>
                  {spotData?.waitings}
                </Typography>
                <Typography sx={{ fontSize: '20px', fontWeight: '500', color: '#000' }}>{t('team')}</Typography>
              </Box>
            </Box>
          </Box>
          <Typography
            onClick={() => onWaitingListClick()}
            sx={{
              fontSize: '16px',
              fontWeight: '600',
              color: '#0F7F85',
              cursor: 'pointer',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}>
            {t('View details')}
          </Typography>
        </Button>
      )}
    </Box>
  );
};

export default GolfSpotUserInfo;
